/**
 * AG-Grid Custom Hooks
 *
 * This module provides React hooks for managing AG-Grid instances.
 * AG-Grid is used throughout the application for data tables.
 *
 * Usage:
 * ```jsx
 * import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
 *
 * function MyGrid() {
 *   const {
 *     AgGridApiRef,
 *     getSelectedRows,
 *     toggleMasterRows,
 *   } = useAgGridApi();
 *
 *   return (
 *     <AgGridReact
 *       ref={AgGridApiRef}
 *       rowData={data}
 *       columnDefs={columns}
 *     />
 *   );
 * }
 * ```
 *
 * @see https://www.ag-grid.com/react-data-grid/ - AG-Grid React Documentation
 * @see src/datastage/components/AGGrid/config/ - Grid column configurations
 */
import { useCallback, useRef, useState } from 'react';

/**
 * Custom hook to manage AG Grid API reference and row expansion state.
 *
 * Provides:
 * - AgGridApiRef: Ref to attach to AgGridReact component
 * - getSelectedRows: Get currently selected rows
 * - toggleMasterRows: Toggle all master/detail rows expanded/collapsed
 * - collapseMasterRows: Collapse all rows
 * - expandMasterRows: Expand all rows
 * - isMasterRowsExpanded: Current expansion state
 *
 * @returns {Object} Grid API utilities
 */
export const useAgGridApi = () => {
    // Ref to store the AG Grid API instance
    const AgGridApiRef = useRef();

    // Track whether master rows are expanded (for master/detail grids)
    const [isMasterRowsExpanded, setIsExpanded] = useState(false);

    /**
     * Gets the currently selected rows from the grid
     * @returns {Array|undefined} Selected row data objects
     */
    const getSelectedRows = useCallback(() => {
        if (!AgGridApiRef.current) return;
        if (!AgGridApiRef.current.api) return;
        if (!AgGridApiRef.current.api.getSelectedRows) return;

        const { api } = AgGridApiRef.current;
        return api.getSelectedRows();
    }, [AgGridApiRef]);

    /**
     * Internal function to toggle row expansion state
     * @param {boolean} toggleTo - True to expand, false to collapse
     */
    const _toggleRows = (toggleTo) => () => {
        try {
            if (AgGridApiRef.current != null) {
                const { api } = AgGridApiRef.current;

                // Verify API methods are available
                if (!api.forEachNode) return;
                if (!api.setRowNodeExpanded) return;
                if (!api.refreshCell) return;

                if (api) {
                    // Iterate through all nodes and set their expansion state
                    api.forEachNode((node) => {
                        node.setExpanded(toggleTo);
                        api.setRowNodeExpanded(node, toggleTo);
                    });

                    // Refresh the cells to reflect the changes
                    api.refreshCells({ force: true });
                }
            }
        } finally {
            setIsExpanded(toggleTo);
        }
    };

    /**
     * Toggles all master rows between expanded and collapsed
     */
    const toggleMasterRows = useCallback(() => {
        if (isMasterRowsExpanded === true) {
            _toggleRows(false)();
        } else {
            _toggleRows(true)();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AgGridApiRef, isMasterRowsExpanded]);

    /** Collapses all master rows */
    const collapseMasterRows = useCallback(_toggleRows(false), [AgGridApiRef]);

    /** Expands all master rows */
    const expandMasterRows = useCallback(_toggleRows(true), [AgGridApiRef]);

    return {
        AgGridApiRef,
        getSelectedRows,
        isMasterRowsExpanded,
        toggleMasterRows,
        collapseMasterRows,
        expandMasterRows,
    };
};
